// Libraries
import * as React from "react"
import { useEffect, useState } from "react"
import { useFlexSearch } from "react-use-flexsearch"
import { graphql, useStaticQuery } from "gatsby"
import moment from "moment"

// Service
import EventService from "../services/event"
import AuthService from "../services/auth"
import EventTypeService from "../services/eventType"

// Components
import Button from "./button"
import EventFilters from "./eventFilters"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faChevronRight, faExclamation, faMapMarkerAlt } from "@fortawesome/pro-solid-svg-icons"
import Link from "gatsby-link"
import { GatsbyImage } from "gatsby-plugin-image"

// utils
import { geoLockingEnabled } from "../utils/env"

export default function EventsList({ searchQuery, events, noFilters = false }) {
	const query = useStaticQuery(graphql`
		{
			allEvent(limit: 500, sort: { fields: starts, order: ASC }, filter: { status_id: { eq: 1 } }) {
				nodes {
					apiId
					id
					name
					logo_gatsby {
						childImageSharp {
							gatsbyImageData(width: 400, aspectRatio: 1, transformOptions: { fit: COVER })
						}
					}
					starts
					ends
					slug
					status_id
					synopsis
					artist {
						name
					}
					venue {
						name
						website_url
					}
					type_id
					fulfillment_type
				}
			}
			localSearchEvents {
				index
				store
			}
		}
	`)

	// Initial search events
	const initialEvents = query?.allEvent?.nodes

	// Flexsearch
	const index = query?.localSearchEvents?.index,
		store = query?.localSearchEvents?.store,
		searchEvents = useFlexSearch(searchQuery, index, store, {
			suggest: true,
		})

	// Check whether to use initial events or searched events
	const [eventsState, setEvents] = useState(!searchQuery ? initialEvents : searchEvents)

	useEffect(() => {
		setEvents(!searchQuery ? initialEvents : searchEvents)
	}, [searchQuery])

	// Set initial state of geo-locked events
	const [geoLocked, setGeolocked] = useState([])

	// Initialise new instance of the events service
	const eventService = new EventService()

	// Initialise new instance of the events service
	const eventTypesService = new EventTypeService()

	// Initialise new instance of the auth service
	const authService = new AuthService()

	const [eventTypes, setEventTypes] = useState([])

	useEffect(() => {
		// We only need geo-locked events if the user is logged in
		authService.isLoggedIn().then(isLoggedIn => {
			if (geoLockingEnabled && isLoggedIn) {
				// Get events at runtime to determine whether each of them are geo-locked to the user
				eventService.get().then(response => {
					// Filter by geo-locked
					let geoLocked = response?.data.filter(event => event?.active_geo_lock === 1),
						// Get IDs of geo-locked events
						geoLockedIds = geoLocked.map(test => test.id)

					// Set state of geo-locked events
					setGeolocked(geoLockedIds)
				})
			}
		})

		eventTypesService.get().then(response => {
			let eventTypes = response?.data.map(eventType => eventType)
			setEventTypes(eventTypes)
		})
	}, [])

	const isGeolocked = id => {
		return geoLockingEnabled && geoLocked.includes(id)
	}

	let eventsVenues = initialEvents.map(event => {
		if (event?.venue?.name) {
			return event.venue.name
		}
		return null
	})

	eventsVenues = removeDuplicates(eventsVenues)

	eventsVenues = sortByAlphab(eventsVenues)

	const eventVenuesMapped = eventsVenues.map(event => {
		return { name: event }
	})

	const [searchArtist, setSearchArtist] = useState(null)

	const searchArtistHandleChange = event => {
		const searchArtistQuery = event.target.value
		setSearchArtist(searchArtistQuery)
	}

	const artistsData = React.useMemo(() => {
		let artists = removeDuplicatesByIndex(initialEvents, "artist.name")

		if (searchArtist !== null && searchArtist !== "") {
			artists = artists.filter(event => {
				const artistName = event.artist.name.toLowerCase()
				return artistName.includes(searchArtist.toLowerCase())
			})
		}

		let artistsGrouped = artists.reduce((r, e) => {
			// get first letter of name of current element
			let group = e.artist.name[0]
			// if there is no property in accumulator with this letter create it
			if (!r[group]) r[group] = { group, entries: [e] }
			// if there is push current element to children array for that letter
			else r[group].entries.push(e)
			// return accumulator
			return r
		}, {})

		return Object.entries(artistsGrouped).sort(([a], [b]) => a.localeCompare(b))
	}, [initialEvents, searchArtist])

	// since data at this point is an object, to get array of values
	// we use Object.values method
	let eventsArtists = Object.values(artistsData)

	function removeDuplicatesByIndex(data, key) {
		const keySplit = key.split(".")
		if (keySplit.length > 1) {
			return data.filter(
				(value, index) =>
					index ===
					data.findIndex(elem => elem[keySplit[0]][keySplit[1]] === value[keySplit[0]][keySplit[1]]),
			)
		} else {
			return data.filter((value, index) => index === data.findIndex(elem => elem[key] === value[key]))
		}
	}

	function removeDuplicates(data) {
		return data.filter((value, index) => data.indexOf(value) === index)
	}

	function sortByAlphab(data) {
		return data.sort((a, b) => a.localeCompare(b))
	}

	const filters = [
		{
			class: "reset",
			name: "Remove all filters",
			active: false,
			showFilterIcon: false,
			options: { list: [], showAll: false },
		},
		{
			class: "type",
			name: "Type",
			active: false,
			showFilterIcon: true,
			options: {
				list: eventTypes /*[{'name': 'group1', 'entries':[{'name': 'test1'},{'name': 'test2'}]}, {'name': 'group2', 'entries':[{'name': 'test0'},{'name': 'test1'},{'name': 'test2'}]}]*/,
				showAll: true,
			},
		},
		{
			class: "artist",
			name: "Artist",
			active: false,
			showFilterIcon: true,
			options: {
				list: eventsArtists /*[{'name': 'A', 'entries':[{'name': 'test1'},{'name': 'test2'}]}, {'name': 'B', 'entries':[{'name': 'test0'},{'name': 'test1'},{'name': 'test2'}]}]*/,
				showAll: false,
			},
		},
		{
			class: "venue",
			name: "Venue",
			active: false,
			showFilterIcon: true,
			options: { list: eventVenuesMapped /*[{'name': 'test1'},{'name': 'test2'}]*/, showAll: true },
		},
		{ class: "date", name: "Date", active: true, showFilterIcon: true, options: { list: [], showAll: false } },
	]

	const [dateFromValue, setDateFromValue] = useState(null)
	const [dateToValue, setDateToValue] = useState(null)

	const emptyFilterState = {
		type: [],
		artist: [],
		venue: [],
		date: {
			from: null,
			to: null,
		},
	}

	const isBrowser = typeof window !== "undefined"
	const localStorageFilters = isBrowser && localStorage.getItem("CfC-FilterState")
	const defaultFilterState = localStorageFilters ? JSON.parse(localStorageFilters) : emptyFilterState
	const [filterState, setFilterState] = useState(defaultFilterState)

	const filterDropdowns = {
		type: false,
		artist: false,
		venue: false,
		date: false,
	}

	const [filterDropdownActive, setFilterDropdownActive] = useState(filterDropdowns)

	//specific to showing actively filtered item - to show filter icon
	const [filtersActive, setFiltersActive] = useState({
		type: defaultFilterState.type.length > 0,
		artist: defaultFilterState.artist.length > 0,
		venue: defaultFilterState.venue.length > 0,
		date: defaultFilterState.date.from || defaultFilterState.date.to,
	})

	const resetFilterState = () => {
		setFilterStateAndLocalStorage(emptyFilterState)
		setFilterDropdownActive(filterDropdowns)
		setEvents(!searchQuery ? initialEvents : searchEvents)
	}

	const dateHandler = () => {
		const dateHasValueSet = dateFromValue != null || dateToValue != null
		setFiltersActive({ ...filtersActive, date: dateHasValueSet })
	}

	const dateFromValueOnChange = value => {
		setDateFromValue(value)
		setFilterStateAndLocalStorage({ ...filterState, date: { ...filterState.date, from: value } })
	}

	const dateToValueOnChange = value => {
		setDateToValue(value)
		setFilterStateAndLocalStorage({ ...filterState, date: { ...filterState.date, to: value } })
	}

	const [hasFilterState, setHasFilterState] = useState(false)

	function filterByDate(eventStart, eventEnds, eventStartState, eventEndState) {
		return eventStart.getTime() >= eventStartState.getTime() && eventEnds.getTime() <= eventEndState.getTime()
	}

	function filterByToDate(eventEnds, eventEndState) {
		//just to
		return eventEnds.getTime() <= eventEndState.getTime()
	}

	function filterByFromDate(eventStart, eventStartState) {
		//just from
		return eventStart.getTime() >= eventStartState.getTime()
	}

	useEffect(() => {
		if (filterState.date.from != null || filterState.date.to != null) {
			dateHandler()
		} else if (filterState.date.from == null && filterState.date.to == null) {
			setFiltersActive({ ...filtersActive, date: false })
		}

		let filteredEvents = []

		if (
			filterState.type.length > 0 ||
			filterState.artist.length > 0 ||
			filterState.type.length > 0 ||
			filterState.venue.length > 0 ||
			filterState.date.from != null ||
			filterState.date.to != null
		) {
			filteredEvents = initialEvents.filter(event => {
				let dateCondition = false

				if (filterState.date.from != null || filterState.date.to != null) {
					var eventStart = new Date(event.starts)
					var eventEnds = new Date(event.ends)
					var eventStartState = filterState.date.from
					var eventEndState = filterState.date.to

					if (eventStartState && eventEndState) {
						dateCondition = filterByDate(eventStart, eventEnds, eventStartState, eventEndState)
					} else if (eventStartState != null && eventEndState == null) {
						dateCondition = filterByFromDate(eventStart, eventStartState)
					} else if (eventEndState != null && eventStartState == null) {
						dateCondition = filterByToDate(eventEnds, eventEndState)
					} else {
						dateCondition = false
					}
				}

				return (
					(filterState.type.length === 0 || filterState.type.includes(event.type_id)) &&
					(filterState.artist.length === 0 || filterState.artist.includes(event.artist.name)) &&
					(filterState.venue.length === 0 || filterState.venue.includes(event.venue.name)) &&
					((filterState.date.from == null && filterState.date.to == null) || dateCondition)
				)
			})
			setEvents(filteredEvents)
		} else if (
			filterState.type.length === 0 &&
			filterState.artist.length === 0 &&
			filterState.type.length === 0 &&
			filterState.venue.length === 0 &&
			filterState.date.from === null &&
			filterState.date.to === null
		) {
			setEvents(initialEvents)
		}
	}, [filterState])

	useEffect(() => {
		if (filtersActive.date || filtersActive.artist || filtersActive.type || filtersActive.venue) {
			setHasFilterState(true)
		} else {
			setHasFilterState(false)
		}
	}, [filtersActive])

	function resetFilters() {
		setDateToValue(null)
		setDateFromValue(null)
		resetFilterState()
		setFiltersActive({
			type: false,
			artist: false,
			venue: false,
			date: false,
		})
		setHasFilterState(false)
	}

	function setFilterStateAndLocalStorage(filterState) {
		setFilterState(filterState)
		localStorage.setItem("CfC-FilterState", JSON.stringify(filterState))
	}

	const eventsToMap = events ? events : eventsState

	return (
		<>
			{!noFilters && (
				<EventFilters
					filterDropdowns={filterDropdowns}
					filtersActive={filtersActive}
					resetFilterState={resetFilterState}
					dateFromValue={dateFromValue}
					setDateFromValue={setDateFromValue}
					dateFromValueOnChange={dateFromValueOnChange}
					dateToValue={dateToValue}
					setDateToValue={setDateToValue}
					dateToValueOnChange={dateToValueOnChange}
					filters={filters}
					setFiltersActive={setFiltersActive}
					hasFilterState={hasFilterState}
					setHasFilterState={setHasFilterState}
					filterState={filterState}
					setFilterState={setFilterStateAndLocalStorage}
					filterDropdownActive={filterDropdownActive}
					setFilterDropdownActive={setFilterDropdownActive}
					searchArtist={searchArtist}
					searchArtistHandleChange={searchArtistHandleChange}
				/>
			)}
			<div className="event-list">
				<div className="container">
					<ul>
						{eventsToMap.map(event => {
							let className = `event event--fulfillment-${event?.fulfillment_type}`
							if (isGeolocked(event?.apiId)) {
								className += " event--geolocked"
							}

							const ctaText = event?.fulfillment_type === "papering" ? "Buy Tickets" : "See Event"

							return (
								<li className={className} itemScope itemType="https://schema.org/Event">
									<div className="event__title" itemProp="name">
										<span>{event?.name}</span>
									</div>

									<div className="event__image">
										{event?.logo_gatsby && (
											<GatsbyImage
												image={event.logo_gatsby.childImageSharp.gatsbyImageData}
												alt={`${event?.artist?.name || event?.artist_name} - ${event?.name}`}
												itemProp="image"
											/>
										)}
									</div>

									<div
										className="event__artist"
										itemProp="performer"
										itemType="https://schema.org/PerformingGroup"
									>
										<span>{event?.artist?.name || event?.artist_name}</span>
									</div>

									<div className="event__meta">
										<span
											className="event__meta__venue"
											itemScope
											itemProp="location"
											itemType="https://schema.org/Place"
										>
											<span itemProp="name">{event?.venue?.name || event?.venue_name}</span>
										</span>
										<span
											className="event__meta__date"
											itemProp="startDate"
											content={moment(event?.starts).toISOString()}
										>
											{moment(event?.starts).format("MMMM D, Y")}
										</span>
									</div>

									<div className="event__cta">
										<div className="see-event">
											<Button to={`/events/${event?.slug}`} disabled={isGeolocked(event?.apiId)}>
												{ctaText}
											</Button>
										</div>
										<div className="see-event--short">
											<Link to={`/events/${event?.slug}`} disabled={isGeolocked(event?.apiId)}>
												<FontAwesomeIcon icon={faChevronRight} />
											</Link>
										</div>

										{isGeolocked(event?.apiId) && (
											<span className="event__cta__outofrange">
												<FontAwesomeIcon icon={faMapMarkerAlt} />
												<p className="event__cta__outofrange__message">
													This event is out of range for your postcode{" "}
													<FontAwesomeIcon icon={faExclamation} />
												</p>
											</span>
										)}
									</div>
									<span itemProp="eventStatus" content="https://schema.org/EventScheduled" />
									<span
										itemProp="eventAttendanceMode"
										content="https://schema.org/OfflineEventAttendanceMode"
									/>
									<span itemProp="description" content={event?.synopsis} />
									<span itemProp="endDate" content={moment(event?.ends).toISOString()} />
									<span itemProp="organizer" itemScope itemType="https://schema.org/Organization">
										<span itemProp="name" content={event?.venue?.name || event?.venue_name} />
										<span
											itemProp="url"
											content={event?.venue?.website_url || event?.venue_website_url}
										/>
									</span>
								</li>
							)
						})}

						{!eventsToMap.length && (
							<h3>
								No events found {searchQuery && `using the search term "${searchQuery}"`}
								{hasFilterState && (
									<button className="remove-filters" onClick={() => resetFilters()}>
										{" "}
										- Remove all filters
									</button>
								)}
							</h3>
						)}
					</ul>
				</div>
			</div>
		</>
	)
}
